import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ALL_TYPES,
  type AspectRatio,
  type CreativeType,
  type Language,
  type Theme,
  type Term,
} from "@acs/shared";
import { api } from "../api";

const ALL_LANGS: Language[] = ["EN", "ES", "FR", "DE", "PT-BR"];
const ALL_SIZES: AspectRatio[] = ["1:1", "9:16", "4:5", "16:9"];

export function NewCreative({ productId }: { productId: string }) {
  const navigate = useNavigate();
  const [terms, setTerms] = useState<Term[]>([]);
  const [activeTypes, setActiveTypes] = useState<CreativeType[]>([]);
  const [productName, setProductName] = useState("");

  // form state
  const [termId, setTermId] = useState("");
  const [types, setTypes] = useState<CreativeType[]>([]);
  const [themes, setThemes] = useState<Theme[]>(["light", "dark"]);
  const [languages, setLanguages] = useState<Language[]>(["EN"]);
  const [count, setCount] = useState(5);
  const [sizes, setSizes] = useState<AspectRatio[]>(["9:16", "1:1"]);
  const [textProvider, setTextProvider] = useState<"claude" | "gemini">("claude");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [t, p] = await Promise.all([
        api.listTerms(productId),
        api.getProduct(productId),
      ]);
      setTerms(t.terms);
      setProductName(p.product.name);
      const at = (p.product.types ?? []).filter((x) =>
        (ALL_TYPES as string[]).includes(x),
      ) as CreativeType[];
      setActiveTypes(at);
    })();
  }, [productId]);

  const totalIds = useMemo(
    () => count * types.length * 2 * themes.length,
    [count, types.length, themes.length],
  );
  const totalFiles = useMemo(
    () => totalIds * languages.length * sizes.length,
    [totalIds, languages.length, sizes.length],
  );

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await api.createJob({
        product_id: productId,
        term_id: termId,
        types,
        themes,
        languages,
        count,
        sizes,
        kind: "image",
        text_provider: textProvider,
      });
      navigate(`/products/${productId}/queue`);
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit =
    termId &&
    types.length > 0 &&
    themes.length > 0 &&
    languages.length > 0 &&
    sizes.length > 0 &&
    count >= 1;

  const isUnconfigured = terms.length === 0 || activeTypes.length === 0;

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-xl font-bold mb-1">{productName} — Yeni Kreatif</h1>
      <p className="text-sm text-zinc-500 mb-6">
        Termin → Type → Theme → Dil → Angle → Boyut. Üret'e bastığında arka plan otomatik üretir.
      </p>

      {isUnconfigured && (
        <div className="mb-5 bg-amber-50 border border-amber-300 rounded p-4 text-sm">
          <div className="font-semibold text-amber-900 mb-1">
            Ürün ayarları eksik — önce bunları tanımla:
          </div>
          <ul className="list-disc list-inside text-amber-800 space-y-0.5">
            {terms.length === 0 && <li>Termin listesi boş (en az bir termin ekle).</li>}
            {activeTypes.length === 0 && <li>Aktif Type seti boş (en az bir type seç).</li>}
          </ul>
          <Link
            to={`/products/${productId}/settings`}
            className="inline-block mt-2 text-accent font-medium hover:underline"
          >
            → Ürün Ayarları'na git
          </Link>
        </div>
      )}

      <div
        className={`space-y-5 bg-white border rounded-lg p-6 ${
          isUnconfigured ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <Row label="Termin">
          <select
            value={termId}
            onChange={(e) => setTermId(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm w-72"
          >
            <option value="">— seç —</option>
            {terms.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {terms.length === 0 && (
            <span className="text-xs text-zinc-500 ml-3">
              Termin yok — Ürün Ayarları'ndan ekleyin.
            </span>
          )}
        </Row>

        <Row label="Type">
          <Multi
            options={activeTypes.length ? activeTypes : ALL_TYPES}
            selected={types}
            onChange={(v) => setTypes(v as CreativeType[])}
          />
          {activeTypes.length === 0 && (
            <span className="text-xs text-zinc-500 ml-3 block mt-1">
              Aktif type seti tanımlı değil — ürün ayarlarından seçin.
            </span>
          )}
        </Row>

        <Row label="Theme">
          <Multi
            options={["light", "dark"]}
            selected={themes}
            onChange={(v) => setThemes(v as Theme[])}
          />
        </Row>

        <Row label="Diller">
          <Multi
            options={ALL_LANGS}
            selected={languages}
            onChange={(v) => setLanguages(v as Language[])}
          />
        </Row>

        <Row label="Angle (adet)">
          <input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
            className="border rounded px-2 py-1.5 text-sm w-20"
          />
        </Row>

        <Row label="Boyutlar">
          <Multi
            options={ALL_SIZES}
            selected={sizes}
            onChange={(v) => setSizes(v as AspectRatio[])}
          />
        </Row>

        <Row label="Text AI">
          <select
            value={textProvider}
            onChange={(e) => setTextProvider(e.target.value as "claude" | "gemini")}
            className="border rounded px-2 py-1.5 text-sm w-48"
          >
            <option value="claude">Claude Sonnet 4.5</option>
            <option value="gemini">Gemini 2.5 Pro</option>
          </select>
          <span className="text-xs text-zinc-500 ml-3">
            angle/stage/hook/body/cta üreten model
          </span>
        </Row>

        <div className="border-t pt-4 text-sm text-zinc-600">
          Tahmini çıktı:{" "}
          <span className="font-medium text-zinc-900">
            {count} angle × {types.length} type × 2 stage × {themes.length} theme = {totalIds} ID
          </span>
          , {totalFiles} dosya
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-end">
          <button
            disabled={!canSubmit || submitting}
            onClick={submit}
            className="px-4 py-2 rounded bg-accent text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting ? "Gönderiliyor…" : "Üret"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start">
      <div className="w-32 text-sm font-medium text-zinc-700 mt-1.5">{label}</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Multi<T extends string>({
  options,
  selected,
  onChange,
}: {
  options: readonly T[];
  selected: T[];
  onChange: (v: T[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = selected.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() =>
              onChange(on ? selected.filter((x) => x !== o) : [...selected, o])
            }
            className={`px-3 py-1 text-sm rounded border ${
              on
                ? "bg-accent text-white border-accent"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}
